import React, { useState, useEffect, useCallback } from 'react'

import { useQueryClient } from '@tanstack/react-query'
import { useSearchParams } from 'react-router-dom'

import { useTranslation } from '@/core/i18n/useTranslation'
import { useCardPaymentMethodId } from '@/features/account/billing/hooks/useCardPaymentMethodId'
import { Button } from '@/shared/components/atoms/Button'

import { BankDetailsView } from './BankDetailsView'
import { PAYMENT_METHOD_TYPES } from './PaymentMethods.constants'
import { PaymentMethodsProps, PaymentMethodType } from './PaymentMethods.types'
import { PaymentMethodsHeader } from './PaymentMethodsHeader'
import { PaymentMethodsList } from './PaymentMethodsList'
import { PaymentMethodTabs } from './PaymentMethodTabs'
import { StripeFormRenderer } from './StripeFormRenderer'
import { usePaymentMethodsData } from '../hooks/usePaymentMethodsData'
import { usePaymentMethodSelection } from '../hooks/usePaymentMethodSelection'

/**
 * Main component for managing payment methods
 * Handles both list view and add/edit form view
 */
const PaymentMethods: React.FC<PaymentMethodsProps> = ({
	onBack,
	hideHeader = false,
	showAddFormByDefault = false,
	defaultPaymentMethodType,
	forceShowAutomaticPayment = false,
	onSuccess,
	onSubmittingChange,
	formId,
	hideInternalButtons,
	showManualPayment,
}) => {
	const { t } = useTranslation('common')
	const [searchParams, setSearchParams] = useSearchParams()
	const actionParam = searchParams.get('action')
	const methodParam = searchParams.get('method') as PaymentMethodType
	const queryClient = useQueryClient()

	const [showAddForm, setShowAddForm] = useState(
		showAddFormByDefault || actionParam === 'add'
	)
	const [hasStoredPaymentMethods, setHasStoredPaymentMethods] = useState(true)

	const {
		accountId,
		bankDetails,
		companyTaxId,
		billingSystemCountry,
		showAutomaticPayment,
		hasSEPA,
	} = usePaymentMethodsData()

	// Determine initial payment method type:
	// - If automatic payment is available AND we're in "add" mode, default to card
	// - Otherwise use the provided default or URL param
	const getInitialPaymentMethodType = (): PaymentMethodType => {
		if (methodParam) return methodParam
		if (defaultPaymentMethodType) return defaultPaymentMethodType
		// When adding a payment method and automatic payment is available, default to card
		if (
			(showAddFormByDefault || actionParam === 'add') &&
			(showAutomaticPayment || forceShowAutomaticPayment)
		) {
			return PAYMENT_METHOD_TYPES.CARD
		}
		return PAYMENT_METHOD_TYPES.BANK
	}

	const [paymentMethodType, setPaymentMethodType] = useState<PaymentMethodType>(
		getInitialPaymentMethodType()
	)

	// Sync state with URL params (but respect showAddFormByDefault)
	useEffect(() => {
		if (!showAddFormByDefault) {
			setShowAddForm(actionParam === 'add')
		}
	}, [actionParam, showAddFormByDefault])

	// Update payment method type when URL params or automatic payment availability changes
	useEffect(() => {
		if (methodParam) {
			setPaymentMethodType(methodParam)
		} else if (defaultPaymentMethodType) {
			setPaymentMethodType(defaultPaymentMethodType)
		} else if (
			showAddForm &&
			(showAutomaticPayment || forceShowAutomaticPayment)
		) {
			// In add mode with automatic payment available, default to card
			setPaymentMethodType(PAYMENT_METHOD_TYPES.CARD)
		}
	}, [
		methodParam,
		showAddForm,
		defaultPaymentMethodType,
		showAutomaticPayment,
		forceShowAutomaticPayment,
	])

	// Reset to 'bank' only if automatic payment is not available AND we're on card
	useEffect(() => {
		if (
			!showAutomaticPayment &&
			!forceShowAutomaticPayment &&
			paymentMethodType === PAYMENT_METHOD_TYPES.CARD
		) {
			setPaymentMethodType(PAYMENT_METHOD_TYPES.BANK)
		}
	}, [showAutomaticPayment, forceShowAutomaticPayment, paymentMethodType])

	const handleBackToList = useCallback(() => {
		setSearchParams((prev) => {
			const newParams = new URLSearchParams(prev)
			newParams.delete('action')
			newParams.delete('method')
			return newParams
		})
	}, [setSearchParams])

	const handleSuccess = useCallback(() => {
		queryClient.invalidateQueries({ queryKey: ['paymentTokens'] })
		if (onSuccess) {
			onSuccess()
		} else {
			handleBackToList()
		}
	}, [queryClient, handleBackToList, onSuccess])

	const handlePaymentMethodTypeChange = useCallback(
		(type: PaymentMethodType) => {
			setSearchParams((prev) => {
				const newParams = new URLSearchParams(prev)
				newParams.set('method', type)
				return newParams
			})
		},
		[setSearchParams]
	)

	const handleAddPaymentMethod = useCallback(() => {
		setSearchParams((prev) => {
			const newParams = new URLSearchParams(prev)
			newParams.set('action', 'add')
			// Default to 'card' if automatic payment is available, otherwise 'bank'
			if (showAutomaticPayment || forceShowAutomaticPayment) {
				newParams.set('method', 'card')
			} else {
				newParams.set('method', 'bank')
			}
			return newParams
		})
	}, [setSearchParams, showAutomaticPayment, forceShowAutomaticPayment])

	const {
		stripePromise,
		clientSecret,
		getElementsOptions,
		availablePaymentMethods,
		isLoadingPaymentMethods,
		isCreatingCardIntent,
		isCreatingSepaIntent,
		selectedStripePaymentMethodType,
		handleSelectPaymentMethod,
		handleStripeCardSubmit,
		handleCancel,
	} = usePaymentMethodSelection({
		accountId,
		billingSystemCountry,
		onSuccess: handleSuccess,
		paymentMethodType,
	})

	const cardPaymentMethodId = useCardPaymentMethodId()

	const renderPaymentForm = () => {
		if (paymentMethodType === PAYMENT_METHOD_TYPES.BANK) {
			return (
				<BankDetailsView
					bankDetails={bankDetails}
					companyTaxId={companyTaxId}
				/>
			)
		}

		if (paymentMethodType === PAYMENT_METHOD_TYPES.CARD) {
			return (
				<div className="space-y-6">
					<StripeFormRenderer
						paymentMethodType={paymentMethodType}
						cardPaymentMethodId={cardPaymentMethodId}
						accountId={accountId}
						billingSystemCountry={billingSystemCountry}
						stripePromise={stripePromise}
						clientSecret={clientSecret}
						getElementsOptions={getElementsOptions}
						availablePaymentMethods={availablePaymentMethods}
						isLoadingPaymentMethods={isLoadingPaymentMethods}
						isCreatingCardIntent={isCreatingCardIntent}
						isCreatingSepaIntent={isCreatingSepaIntent}
						selectedStripePaymentMethodType={selectedStripePaymentMethodType}
						handleSelectPaymentMethod={handleSelectPaymentMethod}
						handleStripeCardSubmit={handleStripeCardSubmit}
						onSuccess={handleSuccess}
						onCancel={handleCancel}
						onSubmittingChange={onSubmittingChange}
						formId={formId}
						hideInternalButtons={hideInternalButtons}
					/>
				</div>
			)
		}

		return null
	}

	if (!showAddForm) {
		return (
			<div className="w-full">
				{!hideHeader && (
					<PaymentMethodsHeader
						title={t('payment_method.title_list')}
						onBack={onBack}
						showAddButton
						onAddClick={handleAddPaymentMethod}
					/>
				)}
				{hideHeader && (
					<div className="flex justify-end mb-4">
						{hasStoredPaymentMethods && (
							<Button
								onClick={handleAddPaymentMethod}
								className="cursor-pointer"
							>
								{t('payment_method.add_payment_method')}
							</Button>
						)}
					</div>
				)}
				<PaymentMethodsList
					onAddPaymentMethod={handleAddPaymentMethod}
					hasSEPA={hasSEPA}
					showMobileEmptyState={hideHeader}
					onHasPaymentMethodsChange={setHasStoredPaymentMethods}
				/>
			</div>
		)
	}

	return (
		<div className="w-full">
			{!hideHeader && (
				<PaymentMethodsHeader
					title={t('payment_method.title_form')}
					onBack={handleBackToList}
				/>
			)}
			<PaymentMethodTabs
				selectedType={paymentMethodType}
				onTypeChange={handlePaymentMethodTypeChange}
				showAutomaticPayment={showAutomaticPayment || forceShowAutomaticPayment}
				showManualPayment={showManualPayment}
			/>
			{renderPaymentForm()}
		</div>
	)
}

export default PaymentMethods
